import React, { Suspense, lazy, useEffect } from 'react';
import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import Spinner from '../app/shared/Spinner';
import { history } from '..';
import { setNavHeader } from '../actions/session';
import { getLeadPartnerDashboard } from '../utils/getLeadPartnerDashboard';

// ADMIN
const BenefitsAdmin = lazy(() => import('./admin-pages/Benefits/Benefits'));
const AdminsBenefitsDetails = lazy(() =>
  import('./admin-pages/Benefits/BenefitsDetails')
);
const AdminEditBenefit = lazy(() =>
  import('./admin-pages/Benefits/EditBenefit')
);

const AdminBatchUpload = lazy(() =>
  import('./admin-pages/Benefits/BatchUpload')
);

const AdminAddBenefit = lazy(() => import('./admin-pages/Benefits/AddBenefit'));

const AddEditBenefitCategory = lazy(() =>
  import('./admin-pages/Benefits/AddEditBenefitCategory')
);
const ResourcesAdmin = lazy(() => import('./admin-pages/Resources/Resources'));
const AddEditResource = lazy(() =>
  import('./admin-pages/Resources/AddEditResource')
);
const AddResourceItem = lazy(() =>
  import('./admin-pages/Resources/AddResourceItem')
);
const BenefitsOnResourceItem = lazy(() =>
  import('./admin-pages/Resources/BenefitsOnResourceItem')
);

const EditResourceItem = lazy(() =>
  import('./admin-pages/Resources/EditResourceItem')
);

const ResourceItemAdmin = lazy(() =>
  import('./admin-pages/Resources/ResourceItem')
);
const Users = lazy(() => import('./admin-pages/Users/Users'));
const LeadPartners = lazy(() => import('./admin-pages/Users/LeadPartners'));
const Referrals = lazy(() => import('./admin-pages/Referrals/Referrals'));
const ReferralTypes = lazy(() =>
  import('./admin-pages/Referrals/ReferralTypes')
);
const AddReferralType = lazy(() =>
  import('./admin-pages/Referrals/AddReferralType')
);

const EditReferralType = lazy(() =>
  import('./admin-pages/Referrals/EditReferralType')
);
const UserDetails = lazy(() => import('./admin-pages/Users/UserDetails'));
const FaqAdmin = lazy(() => import('./admin-pages/Faq/Faq'));
const AdminsPasswordEdit = lazy(() =>
  import('./admin-pages/Users/PasswordEdit')
);

const AdminsServiceBackgroundEdit = lazy(() =>
  import('./admin-pages/Users/ServiceBackgroundEdit')
);
const AdminsServiceRecognitionEdit = lazy(() =>
  import('./admin-pages/Users/ServiceRecognitionEdit')
);
const AdminsDisabilityInfoEdit = lazy(() =>
  import('./admin-pages/Users/DisabilityInfoEdit')
);
const AdminsAdditionalInfoEdit = lazy(() =>
  import('./admin-pages/Users/AdditionalInfoEdit')
);
const AdminsUserEvents = lazy(() => import('./admin-pages/Users/UserEvents'));

const AdminsUserEventsByUser = lazy(() =>
  import('./admin-pages/Users/UserEventsByUser')
);

const AdminsFeedback = lazy(() => import('./admin-pages/Feedback/Feedback'));

const Statistics = lazy(() => import('./admin-pages/Statistics/Statistics'));
const LeadPartnerStatistics = lazy(() =>
  import('./admin-pages/Statistics/LeadPartnerStatistics')
);
const OrganizationStatistics = lazy(() =>
  import('./admin-pages/Statistics/OrganizationStatistics')
);

const AdminsTwoFactorAuth = lazy(() =>
  import('./admin-pages/TwoFactorAuthentication')
);

// MEMBERS
const Benefits = lazy(() => import('./member-pages/Benefits/Benefits'));
const BenefitsDetails = lazy(() =>
  import('./member-pages/Benefits/BenefitsDetails')
);
const Feedback = lazy(() => import('./member-pages/Feedback/Feedback'));
const ResourcesUser = lazy(() =>
  import('./member-pages/Resources/Resourcesuser')
);

const SavedResourceItems = lazy(() =>
  import('./member-pages/Resources/SavedResourceItems')
);

const ResourceItem = lazy(() =>
  import('./member-pages/Resources/ResourceItem')
);

const EditUserPassword = lazy(() =>
  import('./member-pages/Profile/PasswordEdit')
);

const EditUserDetails = lazy(() =>
  import('./member-pages/Profile/DetailsEdit')
);

const UserProfile = lazy(() => import('./member-pages/Profile/Profile'));
const EditAdditionalInfo = lazy(() =>
  import('./member-pages/Profile/AdditionalInfoEdit')
);
const EditDisabilityInfo = lazy(() =>
  import('./member-pages/Profile/DisabilityInfoEdit')
);
const EditServiceBackground = lazy(() =>
  import('./member-pages/Profile/ServiceBackgroundEdit')
);
const EditServiceRecognition = lazy(() =>
  import('./member-pages/Profile/ServiceRecognitionEdit')
);

const GetConnectedWizard = lazy(() =>
  import('./member-pages/Profile/GetConnectedWizard')
);
const AdditionalInformation = lazy(() =>
  import('./member-pages/Profile/OptInForm/AdditionalInformation')
);

const ProfileCompletion = lazy(() =>
  import('./member-pages/Profile/CompletionWizard')
);

const CtaReferral = lazy(() =>
  import('./member-pages/Profile/OptInForm/CtaReferral.js')
);

// LEAD PARTNERS
const ReferralsLeadPartner = lazy(() =>
  import('./lead-partner-pages/Referrals/Referrals')
);
const ResourceLeadPartner = lazy(() => import('./lead-partner-pages/Resource'));
const OrganizationStatsLeadPartner = lazy(() =>
  import('./lead-partner-pages/Resource/Statistics')
);
const PartnerBenefits = lazy(() =>
  import('./lead-partner-pages/Benefits/Benefits')
);
const PartnerAddBenefit = lazy(() =>
  import('./lead-partner-pages/Benefits/AddBenefit')
);
const PartnerEditBenefit = lazy(() =>
  import('./lead-partner-pages/Benefits/EditBenefit')
);
const PartnerProfile = lazy(() => import('./lead-partner-pages/Profile'));
const PartnerProfileEdit = lazy(() =>
  import('./lead-partner-pages/Profile/DetailsEdit')
);
const TwoFactorSetup = lazy(() =>
  import('./lead-partner-pages/TwoFactorSetup')
);
const PartnerUsers = lazy(() => import('./lead-partner-pages/Users'));
const ClosedCases = lazy(() => import('./lead-partner-pages/ClosedCases'));
const Metrics = lazy(() => import('./lead-partner-pages/Metrics'));
const InviteUsers = lazy(() =>
  import('./lead-partner-pages/Users/InviteUsers')
);

// UNAUTHENTICATED
const Welcome = lazy(() => import('./user-pages/Welcome'));
const Login = lazy(() => import('./user-pages/Login'));
const Register = lazy(() => import('./user-pages/Register'));
const ExternalReferral = lazy(() => import('./user-pages/ExternalReferral'));
const EmailVerification = lazy(() => import('./user-pages/EmailVerification'));
const ChangePassword = lazy(() => import('./user-pages/ChangePassword'));
const ResetPassword = lazy(() => import('./user-pages/ResetPassword'));
const PasswordResetSuccess = lazy(() =>
  import('./user-pages/PasswordResetSuccess')
);
const Faq = lazy(() => import('./member-pages/Faq/Faq'));
const InfoPage = lazy(() => import('./info-page/InfoPage'));
const Support = lazy(() => import('./user-pages/Support'));
const TrustedForm = lazy(() => import('./user-pages/TrustedForm'));
const ContactRequest = lazy(() => import('./user-pages/ContactRequest'));
const ExternalBenefits = lazy(() => import('./user-pages/ExternalBenefits'));

const pages = [
  {
    path: '/about-us',
    component: InfoPage,
  },
  {
    path: '/privacy-policy',
    component: InfoPage,
  },
  {
    path: '/terms-of-use',
    component: InfoPage,
  },
  {
    path: '/support',
    component: Support,
  },
];

const AppRoutes = (props) => {
  const inProduction = process.env.NODE_ENV === 'production';
  const twoFactorDisabled = inProduction && !props.user?.twoFactorAuthEnabled;

  function commonRoutes() {
    return pages.map((page) => (
      <Route
        exact
        key={page.path}
        path={page.path}
        component={page.component}
      />
    ));
  }

  function protectedRouteMember() {
    return (
      <>
        <Switch>
          <Route exact path="/benefits/by-type/:type" component={Benefits} />
          <Route exact path="/benefits" component={Benefits} />
          <Route exact path="/benefits/:id" component={BenefitsDetails} />
          <Route
            path="/shared-benefit/:id"
            render={({ match }) => (
              <Redirect to={`/benefits/${match.params.id}`} />
            )}
          />
          <Route
            exact
            path="/resources/:name"
            render={(props) => (
              <ResourcesUser key={props.match.params.name} {...props} />
            )}
          />

          <Route exact path="/saved-resources" component={SavedResourceItems} />
          <Route exact path="/resources/items/:id" component={ResourceItem} />
          <Route exact path="/connect-with/:recordId" component={CtaReferral} />
          <Route
            exact
            path="/connect-with/wizard/:step"
            component={GetConnectedWizard}
          />
          <Route
            exact
            path="/connect-with/wizard"
            component={GetConnectedWizard}
          />
          <Route
            exact
            path="/profile/completion/:step"
            component={ProfileCompletion}
          />
          <Route exact path="/profile/password" component={EditUserPassword} />
          <Route exact path="/profile/edit" component={EditUserDetails} />
          <Route
            exact
            path="/profile/additional-info"
            component={EditAdditionalInfo}
          />
          <Route
            exact
            path="/profile/disability-info"
            component={EditDisabilityInfo}
          />
          <Route
            exact
            path="/profile/service-background"
            component={EditServiceBackground}
          />
          <Route
            exact
            path="/profile/service-recognition"
            component={EditServiceRecognition}
          />

          <Route exact path="/connect-with" component={GetConnectedWizard} />
          <Route
            exact
            path="/connect-with-contact-info"
            component={AdditionalInformation}
          />
          <Route exact path="/profile" component={UserProfile} />
          <Route exact path="/feedback" component={Feedback} />
          <Route exact path="/faq" component={Faq} />
          {commonRoutes()}
          {/*
        share with friends
      */}
          <Redirect
            to={
              history.location.pathname === '/benefits'
                ? `${history.location.pathname}${history.location.search}`
                : '/benefits'
            }
          />
        </Switch>
      </>
    );
  }

  function protectedRouteAdmin() {
    return (
      <Switch>
        <Route exact path="/benefits" component={BenefitsAdmin} />
        <Route exact path="/benefits/:id" component={AdminsBenefitsDetails} />
        <Route exact path="/add-benefit" component={AdminAddBenefit} />
        <Route exact path="/edit-benefit/:id" component={AdminEditBenefit} />
        <Route
          exact
          path="/batch-upload-benefits"
          component={AdminBatchUpload}
        />
        <Route
          exact
          path="/add-benefit-category"
          component={AddEditBenefitCategory}
        />
        <Route
          exact
          path="/edit-benefit-category"
          component={AddEditBenefitCategory}
        />
        <Route exact path="/resources" component={ResourcesAdmin} />
        <Route exact path="/add-resource" component={AddEditResource} />
        <Route exact path="/edit-resource" component={AddEditResource} />
        <Route exact path="/resources/:name" component={ResourceItemAdmin} />
        <Route exact path="/add-resource-item" component={AddResourceItem} />
        <Route
          exact
          path="/items/:id/benefits"
          component={BenefitsOnResourceItem}
        />
        <Route
          exact
          path="/edit-resource-item/:id"
          component={EditResourceItem}
        />
        <Route exact path="/admins/users" component={Users} />
        <Route exact path="/admins/lead-partners" component={LeadPartners} />
        <Route exact path="/admins/users/:id" component={UserDetails} />
        <Route exact path="/admins/user-events" component={AdminsUserEvents} />
        <Route
          exact
          path="/admins/user-events/:id"
          component={AdminsUserEventsByUser}
        />
        <Route
          exact
          path="/admins/users/:id/password"
          component={AdminsPasswordEdit}
        />
        <Route
          exact
          path="/admins/users/:id/service-background"
          component={AdminsServiceBackgroundEdit}
        />
        <Route
          exact
          path="/admins/users/:id/service-recognition"
          component={AdminsServiceRecognitionEdit}
        />
        <Route
          exact
          path="/admins/users/:id/disability-info"
          component={AdminsDisabilityInfoEdit}
        />
        <Route
          exact
          path="/admins/users/:id/additional-info"
          component={AdminsAdditionalInfoEdit}
        />
        <Route exact path="/admins/referrals" component={Referrals} />

        <Route
          exact
          path="/admins/referrals/referral-types"
          component={ReferralTypes}
        />
        <Route
          exact
          path="/admins/referrals/add-referral-type"
          component={AddReferralType}
        />
        <Route
          exact
          path="/admins/referrals/edit-referral-type/:id"
          component={EditReferralType}
        />

        <Route exact path="/feedback" component={AdminsFeedback} />
        <Route exact path="/faq" component={FaqAdmin} />
        <Route exact path="/admins/statistics" component={Statistics} />
        <Route
          exact
          path="/admins/partner-statistics"
          component={LeadPartnerStatistics}
        />
        <Route exact path="/admins/2fa" component={AdminsTwoFactorAuth} />
        <Route
          exact
          path="/admins/organization/stats/:id"
          component={OrganizationStatistics}
        />
        {commonRoutes()}

        <Redirect to="/benefits" />
      </Switch>
    );
  }

  function protectedRouteLeadPartner() {
    const routesToShow = getLeadPartnerDashboard(props.user);
    const routes = [
      {
        path: '/benefits',
        exact: true,
        component: PartnerBenefits,
        condition: routesToShow.includes('Benefits'),
      },
      {
        path: '/benefits/new',
        exact: true,
        component: PartnerAddBenefit,
        condition: routesToShow.includes('Benefits'),
      },
      {
        path: '/benefits/edit/:id',
        exact: true,
        component: PartnerEditBenefit,
        condition: routesToShow.includes('Benefits'),
      },
      {
        path: '/referrals',
        exact: true,
        component: ReferralsLeadPartner,
        condition: routesToShow.includes('Referrals'),
      },
      {
        path: '/resource',
        exact: true,
        component: ResourceLeadPartner,
        condition: true,
      },
      {
        path: '/benefits/:id',
        exact: true,
        component: BenefitsDetails,
        condition: true,
      },
      {
        path: '/statistics',
        exact: true,
        component: OrganizationStatsLeadPartner,
        condition: true,
      },
      {
        path: '/users',
        exact: true,
        component: PartnerUsers,
        condition: routesToShow.includes('Outreach/Users'),
      },
      {
        path: '/metrics',
        exact: true,
        component: Metrics,
        condition: routesToShow.includes('Program Metrics'),
      },
      {
        path: '/closed-cases',
        exact: true,
        component: ClosedCases,
        condition: routesToShow.includes('Advisor Metrics'),
      },
    ];
    return (
      <Switch>
        {commonRoutes()}
        {twoFactorDisabled && (
          <Route exact path="/two-factor-setup" component={TwoFactorSetup} />
        )}
        {twoFactorDisabled && <Redirect to="/two-factor-setup" />}
        {routes
          .filter((route) => route.condition)
          .map(({ path, exact, component }, index) => (
            <Route
              key={index}
              exact={exact}
              path={path}
              component={component}
            />
          ))}
        <Route exact path="/profile" component={PartnerProfile} />
        <Route exact path="/profile/password" component={EditUserPassword} />
        <Route exact path="/profile/edit" component={PartnerProfileEdit} />
        <Route exact path="/invite-users" component={InviteUsers} />
        <Redirect to="/profile" />
      </Switch>
    );
  }

  useLocation();
  useEffect(() => {
    props.setNavHeader(null);
  });
  return (
    <>
      <Suspense fallback={<Spinner />}>
        {props.user !== null ? (
          props.isAdmin ? (
            protectedRouteAdmin()
          ) : props.isLeadPartner ? (
            protectedRouteLeadPartner()
          ) : (
            protectedRouteMember()
          )
        ) : (
          <>
            <Switch>
              <Route path="/login" component={Login} />
              <Route path="/register" component={Register} />
              <Route
                path="/external-referral/:referralKey"
                component={ExternalReferral}
              />
              <Route path="/email-verification" component={EmailVerification} />
              <Route path="/reset-password/:id" component={ResetPassword} />
              <Route path="/shared-benefit/:id" component={ExternalBenefits} />
              <Route path="/trusted-form-agreement" component={TrustedForm} />
              <Route
                path="/reset-password-success"
                component={PasswordResetSuccess}
              />
              <Route path="/change-password" component={ChangePassword} />
              <Route exact path="/faq" component={Faq} />
              <Route exact path="/feedback" component={ContactRequest} />
              <Route exact path="/" component={Welcome} />
              {commonRoutes()}
              <Redirect to="/" />
            </Switch>
          </>
        )}
      </Suspense>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.session.user,
  isAdmin: state.session.isAdmin,
  isLeadPartner: state.session.isLeadPartner,
});

const mapDispatchToProps = (dispatch) => ({
  setNavHeader: (header) => {
    dispatch(setNavHeader(header));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AppRoutes);
